/** Config das métricas do dashboard (estilo Search Console). Onda 18 v2. */

export interface Metric {
  id: string;
  period: string; // data da medição (ISO "2026-07-20")
  isBaseline: boolean;
  revenue: number | null;
  avgTicket: number | null;
  appointments: number | null;
  followers: number | null;
  reach: number | null;
  impressions: number | null;
  profileVisits: number | null;
  engagement: number | null;
  /** Visualizações de stories daquele período — fato medido, entra na série histórica. */
  storyViews: number | null;
  /**
   * Expectativa que a Lara digitou pros próximos dias — NÃO é série
   * histórica (não faz parte de MetricKey/METRIC_DEFS, não entra no
   * gráfico), é a opinião mais recente dela sobre o futuro.
   */
  storyViewsNextDays: number | null;
  goal: number | null;
}

export type MetricKey =
  | "followers" | "reach" | "impressions" | "profileVisits" | "engagement" | "storyViews"
  | "revenue" | "avgTicket" | "appointments";

export interface MetricDef {
  key: MetricKey;
  label: string;
  color: string;
  group: "instagram" | "negocio";
  money?: boolean;
}

/** Cores no espírito do Search Console. */
export const METRIC_DEFS: MetricDef[] = [
  { key: "followers", label: "Seguidores", color: "#4285F4", group: "instagram" },
  { key: "reach", label: "Alcance", color: "#A142F4", group: "instagram" },
  { key: "impressions", label: "Impressões", color: "#24C1E0", group: "instagram" },
  { key: "profileVisits", label: "Visitas ao perfil", color: "#0F9D58", group: "instagram" },
  { key: "engagement", label: "Interações", color: "#F4B400", group: "instagram" },
  { key: "storyViews", label: "Visualizações de stories", color: "#FF6D91", group: "instagram" },
  { key: "revenue", label: "Faturamento", color: "#DB4437", group: "negocio", money: true },
  { key: "avgTicket", label: "Ticket médio", color: "#E8710A", group: "negocio", money: true },
  { key: "appointments", label: "Agendamentos", color: "#8E24AA", group: "negocio" },
];

export interface RangeDef {
  key: string;
  label: string;
  days: number | null;
}
export const RANGES: RangeDef[] = [
  { key: "7d", label: "7 dias", days: 7 },
  { key: "28d", label: "28 dias", days: 28 },
  { key: "3m", label: "3 meses", days: 90 },
  { key: "12m", label: "12 meses", days: 365 },
  { key: "all", label: "Tudo", days: null },
];

const brl = new Intl.NumberFormat("pt-BR", {
  style: "currency", currency: "BRL", maximumFractionDigits: 0,
});
const int = new Intl.NumberFormat("pt-BR");

export function fmtValue(def: MetricDef, n: number): string {
  return def.money === true ? brl.format(n) : int.format(n);
}

export function fmtInt(n: number): string {
  return int.format(Math.round(n));
}

/** "+257/mês", "-40/mês" ou "0/mês" — o crescimento médio de seguidores. */
export function fmtGrowthPerMonth(n: number | null): string {
  if (n === null) return "—";
  const rounded = Math.round(n);
  return `${rounded > 0 ? "+" : ""}${int.format(rounded)}/mês`;
}

/** Mantém só as medições dentro dos últimos `days` (null = tudo). */
export function filterByRange(metrics: Metric[], days: number | null): Metric[] {
  if (days === null) return metrics;
  const cutoff = Date.now() - days * 86_400_000;
  return metrics.filter((m) => new Date(m.period).getTime() >= cutoff);
}

/** Último valor não-nulo de uma métrica (o número do card). */
export function latestValue(metrics: Metric[], key: MetricKey): number | null {
  for (let i = metrics.length - 1; i >= 0; i--) {
    const row = metrics[i];
    if (row === undefined) continue;
    const v = row[key];
    if (v !== null) return v;
  }
  return null;
}
